import { useMutation } from '@tanstack/react-query';
import { fetchData } from '../../../utils';
import useError from '../../useError';
import useSmartlook from '../../useSmartlook';
import { Address } from '../foxentry/FoxentryApiTypes';

function useClientEmployerResponseMutation() {
  const { error } = useSmartlook();
  const { showError } = useError();
  return useMutation<
    {
      rejected: boolean;
    },
    unknown,
    ClientEmployerResponseMutationOptions
  >({
    mutationFn: async (payload) => {
      try {
        const resp = await fetchData('/client/employer/response', {
          method: 'put',
          body: JSON.stringify(payload),
        });

        return resp;
      } catch (e) {
        error(
          '[useClientEmployerResponseMutation][PUT /client/employer/response]',
          e,
        );
        showError();
        throw e;
      }
    },
  });
}

export default useClientEmployerResponseMutation;

type Nace = {
  code: string;
  name: string;
  isPrimary?: boolean | null;
};

type Registration = {
  type: string;
  data: {
    register: {
      code: string | null;
      name: string | null;
      detail: string | null;
    };
    registrator: {
      code: string;
      name: string;
      detail: string | null;
    };
    reference: string | null;
  };
};

type TradeLicence = {
  type: string;
  name: string;
  valid: {
    from: string;
    fromText: string | null;
    to: string | null;
    toText: string | null;
  };
  interrupted: {
    from: string | null;
    to: string | null;
  };
  responsiblePerson: string | null;
  activitySubjects: string[];
};

type Vat = {
  status: string;
  reliability: {
    reliable: boolean;
    unreliableFrom: string | null;
  };
  bankAccounts: {
    number: string;
  }[];
  history: unknown[];
};

type LegalForm = {
  code: string | null;
  name: string | null;
};

type Esa2010 = {
  code: string;
  name: string;
};

type Dates = {
  created: string;
  terminated: string | null;
};

export type ClientEmployerResponseMutationOptions = {
  uuid: number;
  country?: string;
  name: string;
  registrationNumber: number;
  taxNumber?: string | null;
  vatNumber?: string;
  vatNumberSpecial?: string | null;
  address?: {
    data: Address;
  };
  addressOfficial?: {
    data: Address;
  };
  nace: Nace[];
  registrations?: Registration[];
  legalForm?: LegalForm;
  legalFormSpecific?: LegalForm;
  esa2010?: Esa2010;
  dates?: Dates;
  tradeLicences?: TradeLicence[];
  vat?: Vat;
};
